export const DEFAULT_FONT_COLOR = '#000000';
export const DEFAULT_BACKGROUND_COLOR = '#FFFFFF';

// GET /tier/list status filter. Omitting it returns drafts and published together.
export const TIER_STATUS = {
  DRAFT: 0,
  PUBLISHED: 100,
};

// badge heights the list and previews render, with the font and icon scaled to each
export const PREVIEW_SIZES = [
  { size: 40, font: 16, icon: 20, padding: 16, gap: 8 },
  { size: 32, font: 14, icon: 16, padding: 12, gap: 6 },
  { size: 24, font: 12, icon: 12, padding: 8, gap: 4 },
];

export const UNIT = {
  BOOLEAN: 'boolean',
  COUNT: 'count',
  CURRENCY: 'currency',
  ENUM: 'enum',
};

// the account types used elsewhere in the app, see FilterDropdown.vue
const ACCOUNT_TYPE_OPTIONS = [
  { label: 'Storefront', value: 'storefront' },
  { label: 'Links', value: 'linkonly' },
];

// the 14 metrics, in the order given by the table.
// `verb`, `noun`, `affirm` and `sentence_label` only phrase a saved condition on the tier list:
// numeric rows read "{verb} at least|more than {value} {noun}", so each metric keeps its own
// verb instead of the one shared "Earn" that broke the action metrics.
export const CONDITION_FIELDS = [
  {
    value: 'account_creation',
    label: 'Account Creation (Default Tier)',
    unit: UNIT.BOOLEAN,
    affirm: 'Create an account',
  },
  {
    value: 'tiktok_account_connected',
    label: 'TikTok Account Connected',
    unit: UNIT.BOOLEAN,
    affirm: 'Connect TikTok account',
  },
  {
    value: 'instagram_account_connected',
    label: 'Instagram Account Connected',
    unit: UNIT.BOOLEAN,
    affirm: 'Connect Instagram account',
  },
  {
    value: 'branded_content',
    label: 'Branded Content',
    unit: UNIT.COUNT,
    verb: 'Post',
    noun: 'pieces of branded content',
  },
  { value: 'collections_created', label: 'Collections Created', unit: UNIT.COUNT, verb: 'Create', noun: 'collections' },
  { value: 'posts_created', label: 'Posts Created', unit: UNIT.COUNT, verb: 'Create', noun: 'posts' },
  { value: 'products_added', label: 'Products Added', unit: UNIT.COUNT, verb: 'Add', noun: 'products' },
  { value: 'featured_items', label: 'Featured Items', unit: UNIT.COUNT, verb: 'Feature', noun: 'items' },
  { value: 'copied_shortlinks', label: 'Copied Shortlinks', unit: UNIT.COUNT, verb: 'Copy', noun: 'shortlinks' },
  {
    value: 'account_type',
    label: 'Account Type',
    unit: UNIT.ENUM,
    // the sentence says "Account type", the picker's own label stays title cased
    sentence_label: 'Account type',
    options: ACCOUNT_TYPE_OPTIONS,
  },
  { value: 'storefront_views', label: 'Storefront Views', unit: UNIT.COUNT, verb: 'Earn', noun: 'storefront views' },
  { value: 'total_clicks', label: 'Total Clicks', unit: UNIT.COUNT, verb: 'Earn', noun: 'clicks' },
  {
    value: 'total_sales_value',
    label: 'Total Sales Value',
    unit: UNIT.CURRENCY,
    verb: 'Earn',
    // the currency unit puts the $ on the amount, so the noun carries the preposition
    noun: 'in sales value',
  },
  { value: 'total_sales', label: 'Total Sales', unit: UNIT.COUNT, verb: 'Earn', noun: 'sales' },
];

let uid = 0;

function nextKey(prefix) {
  uid += 1;
  return `${prefix}-${uid}`;
}

export function createCondition() {
  return {
    key: nextKey('condition'),
    field: '',
    operator: '',
    value: '',
  };
}

// the one condition the starting tier always carries: signing up
export function startingCondition() {
  return { ...createCondition(), field: 'account_creation', operator: '=', value: true };
}

// count and currency values bind to a text input, booleans and enums stay native
function conditionValueFromApi(item) {
  const unit = fieldUnit(item.field);
  if (unit === UNIT.BOOLEAN || unit === UNIT.ENUM) return item.value;
  return item.value === null || item.value === undefined ? '' : String(item.value);
}

/**
 * Maps one row of GET /tier/list onto the shape the components work with.
 * The only place in the feature that reads the API payload.
 */
export function tierFromApi(raw, index = 0) {
  // note `condition` is singular on the wire, matching the create/update body
  const conditions = (raw.condition || []).map((item) => ({
    ...createCondition(),
    field: item.field ?? '',
    operator: item.operator ?? '',
    value: conditionValueFromApi(item),
  }));

  const rank = raw.rank ?? index + 1;
  // the API is the authority on which row is the default one, there is no way to infer it
  // from a single row: sortTiersByRank puts it last and syncRanks confirms it on edit
  const is_starting = !!raw.is_starting_tier;

  return {
    key: `tier-api-${raw.id ?? index}`,
    id: raw.id ?? null,
    rank,
    is_starting,
    status: raw.status ?? TIER_STATUS.PUBLISHED,
    name: raw.name ?? '',
    description: raw.description ?? '',
    icon: raw.icon_url ?? '',
    icon_file: null,
    icon_removed: false,
    font_color: raw.font_color_hex || DEFAULT_FONT_COLOR,
    background_color: raw.background_color_hex || DEFAULT_BACKGROUND_COLOR,
    conditions: conditions.length ? conditions : [is_starting ? startingCondition() : createCondition()],
    creators_count: raw.creator_count,
    last_edited: raw.updated_at ?? null,
    published_at: raw.published_at ?? null,
  };
}

export function fieldOf(field) {
  return CONDITION_FIELDS.find((item) => item.value === field);
}

export function fieldUnit(field) {
  return fieldOf(field)?.unit || '';
}
